import React from 'react'
import Hls from 'hls.js'
import { setIsRadioBusy, setRadioPlaying } from '@core/Areas/Navigation/Store'
import { setCloseSkyVideoPlayer } from '@core/Areas/SkyVideoPlayer/Store'
import { useAppDispatch } from '@core/Store/hooks'
import { logError } from '@core/Utils'
import { RADIO_STREAM_URL } from '../constants'

// Audio Element is module scoped to allow play while using app
let globalAudioElement: HTMLAudioElement | null = null

export function useWebRadio({
  isEnabled,
  isRadioPlaying,
}: {
  isEnabled: boolean
  isRadioPlaying: boolean
}) {
  const dispatch = useAppDispatch()

  const hlsInstanceRef = React.useRef(getHlsInstance())
  const [audioElement, setAudioElement] = React.useState(globalAudioElement)

  // Synchronize Radio state with UI on mount
  React.useEffect(() => {
    if (!isEnabled || !audioElement) {
      if (!audioElement) dispatch(setRadioPlaying(false))
      return
    }
    dispatch(setRadioPlaying(!audioElement.paused && !!audioElement.currentTime))

    const controller = new AbortController()
    const signal = controller.signal

    const onPauseEvent = () => dispatch(setRadioPlaying(false))
    const onPlayEvent = () => dispatch(setRadioPlaying(true))
    audioElement.addEventListener('pause', onPauseEvent, { signal })
    audioElement.addEventListener('play', onPlayEvent, { signal })
    return () => controller.abort()
  }, [audioElement, isEnabled, dispatch])

  const pause = React.useCallback(() => {
    if (audioElement) {
      audioElement.pause()
      // need to set the src to null or it just keeps downloading forever
      audioElement.src = ''
      audioElement.load()
      hlsInstanceRef.current?.detachMedia()
      hlsInstanceRef.current?.stopLoad()
    }
    dispatch(setRadioPlaying(false))
  }, [audioElement, dispatch])

  const triggerPlayOnAudioElement = React.useCallback(async () => {
    if (!globalAudioElement) return
    dispatch(setIsRadioBusy(true))
    try {
      await globalAudioElement.play()
      dispatch(setRadioPlaying(true))
    } catch (e) {
      pause()
      logError(`Exception when attemping to play Radio: ${e}`, true)
    }
    dispatch(setIsRadioBusy(false))
  }, [pause, dispatch])

  const play = React.useCallback(() => {
    if (!globalAudioElement) {
      globalAudioElement = new Audio(RADIO_STREAM_URL)
      setAudioElement(globalAudioElement)
    }

    if (canPlayHlsNatively(globalAudioElement)) {
      // Prefer native HLS for safari
      globalAudioElement.src = RADIO_STREAM_URL
      globalAudioElement.addEventListener('loadedmetadata', () => triggerPlayOnAudioElement(), {
        once: true,
      })
    } else if (hlsInstanceRef.current) {
      hlsInstanceRef.current.loadSource(RADIO_STREAM_URL)
      hlsInstanceRef.current.attachMedia(globalAudioElement)
      hlsInstanceRef.current.on(Hls.Events.MANIFEST_PARSED, () => triggerPlayOnAudioElement())
    } else {
      logError('Could not start Radio - no supported decoding method', true)
    }

    dispatch(setCloseSkyVideoPlayer())
  }, [dispatch, triggerPlayOnAudioElement, setAudioElement])

  const toggle = React.useCallback(() => {
    isRadioPlaying ? pause() : play()
  }, [pause, play, isRadioPlaying])

  return { toggle, pause }
}

// =============
// Local Helpers
// =============

let hls: Hls | null = null

function canPlayHlsNatively(audioElement: HTMLAudioElement) {
  return audioElement.canPlayType('application/vnd.apple.mpegurl') && 'ManagedMediaSource' in window
}

function getHlsInstance() {
  if (!hls) {
    hls = Hls.isSupported() ? new Hls({ enableWorker: true, debug: false }) : null
    hls?.on(Hls.Events.ERROR, (_, data) => {
      logError(`Exception when attemping to play Radio (hls.js): ${data}`, true)
    })
  }
  return hls
}
